<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { useQuasar } from 'quasar'
import SubscriptionCard from '@/components/subscription/SubscriptionCard.vue'
import SubscriptionPreviewCard from '@/components/subscription/SubscriptionPreviewCard.vue'
import { fetchAvailablePlansAsync, createTransactionAsync, updateSubscriptionPreviewAsync, updateSubscriptionAsync } from '@/apis/subscriptions.api'
import { SUBSCRIPTION_CONSTANTS } from '@/constants/subscription.constants'
import { usePaddle } from '@/composables/paddle'
import i18n from '@/plugins/i18n'
import {useRouter} from 'vue-router'
import { useWebSiteStore } from '@/stores/website.store'

// Props for upgrade handling
const emit = defineEmits(['subscription-upgraded'])

const props = defineProps({
  isUpgrade: {
    type: Boolean,
    default: false
  },
  currentSubscription: {
    type: Object,
    default: null
  }
})

const { init, isReady, pricesPreview, openCheckout } = usePaddle()
const $q = useQuasar()
const t = i18n.global.t
const router = useRouter()
const webSiteStore = useWebSiteStore()

const loading = ref(false)
const selectedDuration = ref(props.isUpgrade ? props.currentSubscription.billing_cycle_frequency : 'monthly')
const quantity = ref(
  props.isUpgrade && props.currentSubscription
    ? (props.currentSubscription.quantity || SUBSCRIPTION_CONSTANTS.MIN_QUANTITY)
    : SUBSCRIPTION_CONSTANTS.MIN_QUANTITY
)
const maxQuantity = ref(SUBSCRIPTION_CONSTANTS.MAX_QUANTITY)
const minQuantity = computed(() => {
  if (props.isUpgrade && props.currentSubscription) {
    return (props.currentSubscription.quantity || SUBSCRIPTION_CONSTANTS.MIN_QUANTITY)
  }
  return SUBSCRIPTION_CONSTANTS.MIN_QUANTITY
})

const previewData = ref(null)
const showCheckout = ref(false)

const loadPricingData = async() => {
  if (!isReady.value || !webSiteStore.isLogged()) return

  const duration = selectedDuration.value
  const q = quantity.value

  loading.value = true

  try {
    const beResponse = await fetchAvailablePlansAsync({ duration, quantity: q, subscription: props.isUpgrade ? props.currentSubscription.id : null })
    const plans = beResponse.data.plans || []

    if (plans.length === 0) {
      previewData.value = []
      return
    }

    // Map over each plan and return the preview data
    const previewPromises = plans.map(async(plan) => {
      const discountIdToApply = plan.discount_id || null

      try {
        let normalizedItem = null

        if (props.isUpgrade) {
          // For upgrades, we need to call backend to get the preview of the subscription update
          const payload = {
            price_id: plan.id,
            quantity: q,
            discount_id: discountIdToApply
          }

          const res = await updateSubscriptionPreviewAsync(props.currentSubscription.id, payload)
          const rawPreviewData = res.data.preview

          normalizedItem = {
            price: {
              id: plan.id,
            },
            product: {
              name: plan.name,
              description: plan.description
            },
            quantity: q,

            _upgradePreviewData: rawPreviewData,
            _discountId: discountIdToApply,
          }
        } else {
          const itemsToPreview = [{
            priceId: plan.id,
            quantity: q
          }]

          const paddleResult = await pricesPreview(itemsToPreview, discountIdToApply)

          const item = paddleResult?.data?.details?.lineItems[0] || null
          normalizedItem = {
            ...item,
            _discountId: discountIdToApply
          }
        }

        return normalizedItem
      } catch (err) {
        console.error(`Preview failed for priceId: ${plan.id}`, err)
        return null
      }
    })

    previewData.value = await Promise.all(previewPromises)
    maxQuantity.value = beResponse.data.meta.slider_max_limit || SUBSCRIPTION_CONSTANTS.MAX_QUANTITY
  } catch (error) {
    console.error(error)
    $q.notify({
      type: 'negative',
      message: t('subscriptions.errors.loadPlansFailed')
    })
    previewData.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  init()
})

watch(isReady, (ready) => {
  if (ready) loadPricingData()
})

watch(selectedDuration, () => {
  loadPricingData()
})

let debounceTimeout = null
watch(quantity, () => {
  if (debounceTimeout) clearTimeout(debounceTimeout)

  debounceTimeout = setTimeout(() => {
    loadPricingData()
  }, 300)
})

const handleSelectService = async(priceId, discountId) => {
  if (props.isUpgrade) {
    $q.dialog({
      title: t('subscriptions.manage.confirmUpdateTitle'),
      message: t('subscriptions.manage.confirmUpdateMessage'),
      cancel: {
        label: t('common.cancel'),
        color: 'grey-7',
        flat: true
      },
      ok: {
        label: t('common.confirm'),
        color: 'primary',
        unelevated: true
      },
      persistent: true
    }).onOk(async() => {
      loading.value = true
      try {
        const payload = {
          price_id: priceId,
          quantity: quantity.value,
          discount_id: discountId || null
        }
        await updateSubscriptionAsync(props.currentSubscription.id, payload)

        emit('subscription-upgraded')
      } catch (error) {
        console.error('Upgrade failed:', error)
        $q.notify({
          type: 'negative',
          message: t('subscriptions.errors.upgradeFailed')
        })
      } finally {
        loading.value = false
        router.push({ name: 'subscriptions-confirmation' })
      }
    })
  } else {
    // Standard checkout flow for new subscriptions
    loading.value = true
    showCheckout.value = true

    try {
      const response = await createTransactionAsync({
        price_id: priceId,
        quantity: quantity.value,
        discount_id: discountId || null
      })
      const transactionId = response.data.transaction_id

      openCheckout(transactionId)
    } catch (error) {
      showCheckout.value = false
      if (error.response?.status === 401) {
        $q.notify({
          type: 'warning',
          message: t('subscriptions.errors.loginRequired')
        })
      } else {
        $q.notify({
          type: 'negative',
          message: t('subscriptions.errors.checkoutFailed')
        })
      }
    } finally {
      loading.value = false
    }
  }
}

onMounted(() => {
  loadPricingData()
})
</script>

<template>
  <q-page class="q-pa-md">
    <div v-show="showCheckout">
      <div class="row items-center q-mb-md">
        <q-btn
          flat
          dense
          color="primary"
          icon="mdi-arrow-left"
          :label="$t('common.back')"
          @click="() => showCheckout = false"
        />
      </div>

      <div class="checkout-container" />
    </div>
    <div v-show="!showCheckout">
      <div
        v-if="!props.isUpgrade"
        class="row items-center justify-center q-mb-md"
      >
        <q-btn-toggle
          v-model="selectedDuration"
          spread
          unelevated
          toggle-color="primary"
          toggle-text-color="white"
          :options="[
            {
              label: $t('subscriptions.monthly'),
              value: 'monthly',
              disable: isUpgrade && currentSubscription.billing_cycle_frequency !== 'monthly'
            },
            {
              label: $t('subscriptions.annual'),
              value: 'yearly',
              disable: isUpgrade && currentSubscription.billing_cycle_frequency !== 'yearly'
            },
            {
              label: $t('subscriptions.lifetime'),
              value: 'lifetime',
              disable: isUpgrade && currentSubscription.billing_cycle_frequency !== 'lifetime'
            }
          ]"
          class="duration-selector"
          :disable="loading "
        />
      </div>

      <q-card>
        <q-card-section
          v-if="!(quantity > maxQuantity)"
        >
          <div class="row items-center">
            <div class="col-auto text-subtitle1 text-weight-bold">
              {{ $t('subscriptions.whatsappNumbers') }}: {{ quantity }}
            </div>
            <q-space />
          </div>
          <div class="row items-center">
            <div
              class="col"
            >
              <q-slider
                v-model=" quantity"
                :min="SUBSCRIPTION_CONSTANTS.MIN_QUANTITY"
                :inner-min="minQuantity"
                :max="maxQuantity"
                :step="1"
                label
                markers
                color="primary"
                :disable="loading"
              />
            </div>
          </div>
        </q-card-section>

        <q-inner-loading :showing="loading ">
          <q-spinner-dots
            size="50px"
            color="primary"
          />
        </q-inner-loading>
      </q-card>

      <div class="q-pt-md">
        <div
          v-if="!loading "
          class="q-gutter-y-md "
        >
          <template
            v-for="item in previewData || []"
            :key="item.price.id"
          >
            <SubscriptionPreviewCard
              v-if="props.isUpgrade"
              :item="item"
              @select="handleSelectService"
            />

            <SubscriptionCard
              v-else
              :item="item"
              @select="handleSelectService"
            />
          </template>

          <q-card
            v-if="!loading && previewData && previewData.length === 0"
            class="text-center q-pa-lg bg-grey-1 dashed-border"
          >
            <q-card-section v-if="!isUpgrade">
              <div class="text-grey-6">
                {{ $t('subscriptions.list.empty') }}
              </div>
            </q-card-section>
            <q-card-section v-else>
              <div class="text-grey-6">
                {{ $t('subscriptions.list.noAvailableUpgrade') }}
              </div>
            </q-card-section>
          </q-card>
        </div>
        <q-inner-loading :showing="loading">
          <q-spinner
            color="primary"
            size="2em"
          />
        </q-inner-loading>
      </div>
    </div>
  </q-page>
</template>
